/**
 * 69.2f, the relay (Foothold, decision 145: relays first): what a squad carries and plants to grow its side's web — a
 * rugged case, a telescoping mast nearly two people tall on three guy lines, a head of two yagi antennas
 * turned toward the relay it links to, a pennant in the team's colour, and a light on top. Readable at the shoulder (the
 * case, the stakes, the antennas), from the diorama (the mast, the pennant and the light) and from orbit (X23's plate,
 * `relay` in ui/commandIcons.ts).
 *
 * - **Planting** (69.2b: a time held still): `setRelayStage(m, k)`, k from 0 (the case just set down, the mast lying
 *   folded along it) to 1 (standing, lit) — the stakes driven, the mast swung up from its collar and run up section by
 *   section, the antennas swung out, the light on. The far tier follows it.
 *   The host feeds it the held time over the plant time; an interrupted plant stays where it stopped.
 * - **The web** (69.2c): `setRelayLink(m, state, t)` — linked, the light steady in the team's colour; unlinked (a
 *   stranded tail), a slow amber blink; off (unbuilt, destroyed) dark. `aimRelay(m, yaw)` turns the head toward its link.
 * - **Forms** (63.4's pattern): `whole`; `damaged` — the top section kinked over, one antenna hanging, a guy line gone,
 *   the light flickers; `destroyed` — the mast down along the ground (`fallen:mast`), the head off beside it, the case
 *   burst open. Named parts for lane A to measure: `case`, `mast`, `head`, `fallen:mast`.
 *
 * Built in person units (a person is 1.8 tall) and scaled by `personHeight / 1.8`; RELAY_MODEL_SIZE holds its footprint
 * and height (the body table reads it), held by tests/relayModel.test.ts. Presentation only.
 */
import * as THREE from 'three';
import { bakeStaticParts, markShared } from './meshBake';
import type { UnitPalette } from './unitPalette';
import type { PersonTier } from './personModel';

export type RelayForm = 'whole' | 'damaged' | 'destroyed';
export const RELAY_FORMS: RelayForm[] = ['whole', 'damaged', 'destroyed'];
export type RelayLink = 'linked' | 'unlinked' | 'off';

export interface RelayModel {
  root: THREE.Group;
  full: THREE.Group;
  silhouette: THREE.Group;
  /** the far tier's mast (it rises with the plant) */
  farMast: THREE.Group;
  /** the case's lid (hinged at the back) */
  lid: THREE.Group;
  /** the stakes the guy lines run to (driven in early in the plant) */
  stakes: THREE.Group[];
  /** the mast's sliding sections, the lowest fixed in the case: each rides inside the one below */
  sections: THREE.Group[];
  /** the guy lines, rebuilt as the mast rises */
  guys: THREE.Mesh[];
  /** the head (turns toward its link) and its two antennas (swing out from along the mast) */
  head: THREE.Group;
  yagis: THREE.Group[];
  light: THREE.Mesh;
  /** the pennant: furled until the mast is up */
  pennant: THREE.Mesh;
  farLight: THREE.Mesh;
  lightMat: THREE.MeshLambertMaterial;
  form: RelayForm;
  stage: number;
  tier: PersonTier;
  palette: UnitPalette;
  /** what the light last showed (setRelayLink: a frame that would show the same sets nothing) */
  shown?: string;
}

/** The case, the mast and the head (person units). */
const CASE = { w: 0.62, h: 0.34, d: 0.44 };
const SECTION = 1.05, SECTIONS = 3;
/** A sliding section sits this share of its length out of the one below: collapsed, and run up. */
const SLIDE = { down: 0.08, up: 0.92 };
const MAST_FOOT = CASE.h - 0.05;                                                // the lowest section stands in the case
const MAST_TOP = MAST_FOOT + SECTION * (1 + SLIDE.up * (SECTIONS - 1));         // ≈ 3.27 standing (the head's crossbar)
const STAKE_R = 1.25, GUY_AT = MAST_FOOT + SECTION * 2 - 0.15;          // the guys run from the second section's top
const AMBER = '#ffb347', DARK = '#1c1c20';

const matCache = new Map<string, THREE.MeshLambertMaterial>();
function mat(color: string): THREE.MeshLambertMaterial {
  let m = matCache.get(color);
  if (!m) { m = markShared(new THREE.MeshLambertMaterial({ color, flatShading: true })); matCache.set(color, m); }
  return m;
}
function box(parent: THREE.Object3D, w: number, h: number, d: number, color: string, x: number, y: number, z: number): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat(color));
  m.position.set(x, y, z);
  parent.add(m);
  return m;
}
function group(parent: THREE.Object3D, x = 0, y = 0, z = 0, name = ''): THREE.Group {
  const g = new THREE.Group(); g.position.set(x, y, z); g.name = name; parent.add(g); return g;
}
/** Place a unit-length box (along y) from a to b, `t` thick. */
function spanTo(m: THREE.Mesh, a: THREE.Vector3, b: THREE.Vector3): void {
  const d = b.clone().sub(a), len = d.length();
  m.position.copy(a).addScaledVector(d, 0.5);
  m.scale.set(1, Math.max(1e-4, len), 1);
  m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), len > 1e-6 ? d.normalize() : new THREE.Vector3(0, 1, 0));
}
const smooth = (x: number) => { const k = Math.max(0, Math.min(1, x)); return k * k * (3 - 2 * k); };

/** One yagi: a boom forward (toward the link) with four elements across it, folded up along the mast until swung out. */
function yagi(parent: THREE.Object3D, x: number, p: UnitPalette): THREE.Group {
  const g = group(parent, x, 0, 0, 'yagi');
  box(g, 0.035, 0.035, 0.62, p.metal, 0, 0, 0.24);
  for (let i = 0; i < 4; i++) box(g, 0.3 - i * 0.035, 0.022, 0.022, p.metal, 0, 0, 0.0 + i * 0.16);
  box(g, 0.07, 0.07, 0.07, p.dark, 0, 0, -0.06);   // the feed
  return g;
}

export function buildRelayModel(p: UnitPalette, personHeight: number, opts: { form?: RelayForm } = {}): RelayModel {
  const form = opts.form ?? 'whole';
  const root = new THREE.Group();
  root.scale.setScalar(personHeight / 1.8);
  const full = group(root);

  // ---- the case: a rugged box in the team's colour, dark corner guards and feet, a handle; inside, a lit panel
  const caseG = group(full, 0, 0, 0, 'case');
  box(caseG, CASE.w, CASE.h - 0.06, CASE.d, p.body, 0, 0.04 + (CASE.h - 0.06) / 2, 0);
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
    box(caseG, 0.07, CASE.h - 0.04, 0.07, p.dark, sx * (CASE.w / 2 - 0.02), CASE.h / 2, sz * (CASE.d / 2 - 0.02));   // corner guards
    box(caseG, 0.08, 0.04, 0.08, DARK, sx * (CASE.w / 2 - 0.06), 0.02, sz * (CASE.d / 2 - 0.06));                   // feet
  }
  box(caseG, 0.5, 0.05, 0.03, p.dark, 0, 0.2, CASE.d / 2 + 0.015);                                                    // a latch strip
  box(caseG, 0.1, 0.06, 0.1, p.metal, 0.17, CASE.h - 0.04, -0.08);                                                      // the mast's collar
  const lid = group(caseG, 0, CASE.h - 0.02, -CASE.d / 2, 'lid');   // hinged at the back edge
  box(lid, CASE.w + 0.02, 0.05, CASE.d + 0.02, p.body, 0, 0.025, CASE.d / 2);
  box(lid, 0.26, 0.04, 0.05, p.dark, 0, 0.065, CASE.d / 2);   // the handle

  // ---- the stakes and the guy lines
  const stakes: THREE.Group[] = [], guys: THREE.Mesh[] = [];
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 + Math.PI / 6;
    const s = group(full, Math.sin(a) * STAKE_R, 0, Math.cos(a) * STAKE_R, 'stake');
    box(s, 0.05, 0.16, 0.05, p.metal, 0, 0.06, 0).userData.thin = true; box(s, 0.09, 0.03, 0.09, p.dark, 0, 0.14, 0).userData.thin = true;
    stakes.push(s);
    const gm = new THREE.Mesh(new THREE.BoxGeometry(0.014, 1, 0.014), mat(DARK)); gm.name = 'guy'; gm.userData.thin = true; full.add(gm); guys.push(gm);   // no target: a line, and a peg in the ground
  }

  // ---- the mast: three sections, each sliding up out of the one below (the collar at the case's back right)
  const mast = group(full, 0.17, MAST_FOOT, -0.08, 'mast');   // pivots at its collar: lying along the case until raised
  const sections: THREE.Group[] = [];
  let parent: THREE.Object3D = mast;
  for (let i = 0; i < SECTIONS; i++) {
    const s = group(parent, 0, 0, 0, `section`);
    const t = 0.075 - i * 0.012;
    box(s, t, SECTION, t, p.metal, 0, SECTION / 2, 0);
    box(s, t + 0.025, 0.05, t + 0.025, p.dark, 0, SECTION - 0.02, 0);   // the clamp at its top
    sections.push(s); parent = s;
  }
  // ---- the head, on the top section: a crossbar, the two yagis, the pennant and the light
  const head = group(sections[SECTIONS - 1], 0, SECTION, 0, 'head');
  box(head, 0.86, 0.045, 0.045, p.metal, 0, 0, 0);
  box(head, 0.12, 0.1, 0.1, p.dark, 0, -0.02, 0);
  const yagis = [yagi(head, -0.4, p), yagi(head, 0.4, p)];
  // the pennant, flying back from the crossbar (a thin solid, so it reads from either side and bakes with the rest)
  const flag = new THREE.Shape(); flag.moveTo(0, 0); flag.lineTo(0, -0.36); flag.lineTo(-0.62, -0.18); flag.lineTo(0, 0);
  const pennant = new THREE.Mesh(new THREE.ExtrudeGeometry(flag, { depth: 0.02, bevelEnabled: false }), mat(p.body));
  pennant.rotation.y = -Math.PI / 2;   // the shape's x along −z (back), its thickness across
  pennant.position.set(0.01, -0.06, -0.05); head.add(pennant);
  const lightMat = new THREE.MeshLambertMaterial({ color: p.flash, emissive: new THREE.Color(p.flash), emissiveIntensity: 0.9 });
  const light = new THREE.Mesh(new THREE.SphereGeometry(0.07, 10, 8), lightMat);
  light.position.set(0, 0.13, 0); head.add(light);
  box(head, 0.03, 0.08, 0.03, p.metal, 0, 0.05, 0);

  // ---- the forms
  if (form === 'damaged') {
    sections[SECTIONS - 1].rotation.z = 0.62;                 // the top section kinked over at its clamp
    yagis[0].rotation.x = 1.35; yagis[0].rotation.z = 0.3;     // one antenna hanging from the crossbar
    guys[2].visible = false; stakes[2].rotation.z = 0.5;        // a guy line gone, its stake pulled over
    lid.rotation.x = -0.45;   // sprung
  } else if (form === 'destroyed') {
    // the mast down along the ground (detached), the head off beside it, the case burst
    mast.removeFromParent();
    const fallen = group(full, 0.3, 0.06, -0.1, 'fallen:mast');
    fallen.add(mast); mast.position.set(0, 0, 0);
    fallen.rotation.order = 'YXZ'; fallen.rotation.set(0, 0.5, -Math.PI / 2 + 0.04);   // lying along the ground, turned
    for (const s of sections.slice(1)) s.position.y = SECTION * SLIDE.up;                // run up when it fell: a mast's full length
    head.removeFromParent(); full.add(head); head.position.set(-1.5, 0, 2.3); head.rotation.set(0.18, 1.1, 0.22);
    yagis[1].rotation.x = 0.9;
    restOnGround(full, head); restOnGround(full, fallen);
    for (const g of guys) g.visible = false;
    stakes[1].rotation.x = 0.9;
    lid.removeFromParent(); full.add(lid); lid.position.set(-0.62, 0, 0.3); lid.rotation.set(Math.PI - 0.15, 0.6, 0.1);
    caseG.rotation.z = 0.18;
    restOnGround(full, lid); restOnGround(full, caseG);
    light.visible = false;
  }

  // ---- the far tier: the case, the mast as a block with its crossbar and a big pennant (rising with the plant), the light
  const silhouette = group(root);
  box(silhouette, CASE.w, CASE.h, CASE.d, p.body, 0, CASE.h / 2, 0);
  const farMast = group(silhouette, 0.17, MAST_FOOT, -0.08, 'far:mast');
  const farLight = new THREE.Mesh(new THREE.SphereGeometry(0.2, 8, 6), lightMat);
  if (form !== 'destroyed') {
    const top = (form === 'damaged' ? MAST_TOP - 0.5 : MAST_TOP) - MAST_FOOT;
    box(farMast, 0.18, top, 0.18, p.metal, 0, top / 2, 0);
    box(farMast, 1.0, 0.12, 0.12, p.metal, 0, top, 0);
    box(farMast, 0.06, 0.5, 0.9, p.body, 0, top - 0.4, -0.45);
    farLight.position.set(0, top + 0.2, 0); farMast.add(farLight);
  } else {
    box(silhouette, 0.2, 0.2, 3.2, p.metal, 0.3, 0.1, -0.1).rotation.y = 0.5;
    farLight.visible = false; silhouette.add(farLight);
  }
  silhouette.visible = false;

  const m: RelayModel = { root, full, silhouette, farMast, lid, stakes, sections, guys, head, yagis, light, pennant, farLight, lightMat, form, stage: 1, tier: 'full', palette: p };
  setRelayStage(m, 1);
  return m;
}

/**
 * The plant (69.2b's time held still), k from 0 to 1: the stakes driven (0.15–0.4), the mast swung
 * up from lying along the case (0.3–0.45), run up section by section (0.45–0.85), the antennas swung out (0.85–0.95),
 * the light on (0.96). A destroyed relay ignores it. Cheap: a few transforms, during a plant only.
 */
export function setRelayStage(m: RelayModel, k: number): void {
  m.stage = Math.max(0, Math.min(1, k));
  if (m.form === 'destroyed') return;
  const s = m.stage;
  for (let i = 0; i < m.stakes.length; i++) {
    const d = smooth((s - 0.15 - i * 0.06) / 0.12);
    m.stakes[i].visible = d > 0; m.stakes[i].scale.set(1, Math.max(1e-3, d), 1);
  }
  // the mast swung up from lying along the case (pivoting at its collar), then each section run up in turn
  // (carried, it lies forward along the top of the closed case — the crossbar level, clear of the lid hinged at the back)
  const mast = m.sections[0].parent!, down = 1 - smooth((s - 0.3) / 0.15);
  mast.rotation.x = Math.PI / 2 * down; mast.position.y = MAST_FOOT + 0.12 * down;
  const run = smooth((s - 0.45) / 0.4) * (SECTIONS - 1);
  for (let i = 1; i < SECTIONS; i++) m.sections[i].position.y = SECTION * (SLIDE.down + (SLIDE.up - SLIDE.down) * Math.max(0, Math.min(1, run - (i - 1))));
  // the antennas: folded up along the mast, then swung out level
  const out = smooth((s - 0.85) / 0.1);
  for (const [i, y] of m.yagis.entries()) if (!(m.form === 'damaged' && i === 0)) y.rotation.x = -Math.PI / 2 * (1 - out);
  // the guy lines, from the second section's top to the stakes, once both are there
  m.full.updateMatrixWorld(true);
  const top = new THREE.Vector3(0, SECTION, 0); m.sections[1].localToWorld(top); m.full.worldToLocal(top);
  for (let i = 0; i < m.guys.length; i++) {
    if (m.form === 'damaged' && i === 2) continue;
    const st = m.stakes[i].position.clone().setY(0.14);
    m.guys[i].visible = s > 0.45;
    spanTo(m.guys[i], st, top);
  }
  m.light.visible = s >= 0.96;
  m.pennant.visible = s >= 0.5;   // furled while the mast is folded and rising out of it
  // the far tier: no mast while it lies folded, then rising with the sections; its light with the near one
  const up = smooth((s - 0.45) / 0.4);
  m.farMast.visible = s >= 0.45; m.farMast.scale.y = Math.max(0.05, 0.3 + 0.7 * up);
  m.farLight.visible = m.light.visible;
}

/** Set `o` down on the ground of `frame` (y 0): lifted or lowered until its lowest point touches. */
function restOnGround(frame: THREE.Object3D, o: THREE.Object3D): void {
  frame.updateMatrixWorld(true);
  const inv = new THREE.Matrix4().copy(frame.matrixWorld).invert(), b = new THREE.Box3();
  o.traverse(c => { if ((c as THREE.Mesh).isMesh) b.union(new THREE.Box3().setFromObject(c, true).applyMatrix4(inv)); });
  if (!b.isEmpty()) o.position.y -= b.min.y + 0.01;
}

/** Turn the head (and its antennas) toward its link: `yaw` radians about the mast, 0 = the model's +z. */
export function aimRelay(m: RelayModel, yaw: number): void {
  if (m.form !== 'destroyed') m.head.rotation.y = yaw;
}

/**
 * The web's state on the light: linked, steady in the team's colour; unlinked (stranded), a slow amber blink — on a
 * quarter second in every 1.2; off, dark. A damaged relay's light flickers whichever it is. Cheap: one colour set a frame.
 */
export function setRelayLink(m: RelayModel, state: RelayLink, t: number): void {
  paintLinkLight(m, m.form === 'damaged', m.stage >= 0.96 && m.form !== 'destroyed' && state !== 'off', state, t);
}

/**
 * The web's rule on any of its lights (a relay's, 69.5 the HQ's root lamp): `on` when it could be lit at all; unlinked, the
 * amber blink; `damaged`, a flicker. Sets nothing when what it shows has not changed (`shown`).
 */
export function paintLinkLight(m: { lightMat: THREE.MeshLambertMaterial; palette: UnitPalette; shown?: string }, damaged: boolean, on: boolean, state: RelayLink, t: number): void {
  const blink = state === 'unlinked' ? (t % 1.2) < 0.25 : true;
  const flicker = damaged ? Math.sin(t * 37) * Math.sin(t * 11.3) > -0.2 : true;
  const lit = on && blink && flicker;
  const c = state === 'unlinked' ? AMBER : m.palette.flash, key = lit ? c : DARK;
  if (m.shown === key) return;
  m.shown = key;
  m.lightMat.color.set(lit ? c : DARK);
  m.lightMat.emissive.set(lit ? c : '#000000');
  m.lightMat.emissiveIntensity = lit ? 1 : 0;
}

/**
 * Bake the static parts into one mesh per moving group: the case, the lid, each stake, each section, the head and its
 * antennas (≈ 10 draws a relay at the full tier; relays are few — two a squad), the far tier two (the case, the mast) and its light.
 */
export function bakeRelayModel(m: RelayModel): number {
  const moving: THREE.Object3D[] = [m.lid, ...m.stakes, ...m.sections, m.head, ...m.yagis];
  const isUnder = (o: THREE.Object3D, r: THREE.Object3D) => { for (let q = o.parent; q; q = q.parent) if (q === r) return true; return false; };
  let n = bakeStaticParts(m.full, { exclude: [...moving, ...m.guys, m.light, m.pennant] }).partsBaked;
  for (const g of moving) n += bakeStaticParts(g, { exclude: [...moving.filter(o => o !== g && isUnder(o, g)), m.light, m.pennant] }).partsBaked;
  m.silhouette.visible = true;
  const farOn = m.farMast.visible; m.farMast.visible = true;   // bakeable only while visible
  n += bakeStaticParts(m.farMast, { exclude: [m.farLight] }).partsBaked;
  n += bakeStaticParts(m.silhouette, { exclude: [m.farMast, m.farLight] }).partsBaked;
  m.farMast.visible = farOn;
  setRelayTier(m, m.tier);
  return n;
}

export function setRelayTier(m: RelayModel, tier: PersonTier): void {
  m.tier = tier;
  m.full.visible = tier === 'full';
  m.silhouette.visible = tier === 'silhouette';
}
